import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { MOCK, isSchemaPending } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import { Modal, Fld } from '../components/Modals.jsx';
import DbPending from '../components/DbPending.jsx';
import { staleRule } from '../lib/screening.js';
import { reqLabel } from '../lib/reqtypes.js';

// TASK 7 — Admin: Analytics + Settings (admin เท่านั้น; views มี where is_admin() ในตัว)

// ── นำเข้าพิกัดจากไฟล์ CSV (ทีละเยอะ) ────────────────────────────────────────
const HS_COLMAP = {
  hs_code: 'hs_code', hs: 'hs_code', พิกัด: 'hs_code', 'พิกัดhs': 'hs_code', รหัส: 'hs_code',
  description_th: 'description_th', คำอธิบาย: 'description_th', คำอธิบายไทย: 'description_th', รายละเอียด: 'description_th', ชื่อ: 'description_th',
  duty_rate: 'duty_rate', อากร: 'duty_rate', อัตราอากร: 'duty_rate', duty: 'duty_rate',
  vat_rate: 'vat_rate', vat: 'vat_rate', ภาษีมูลค่าเพิ่ม: 'vat_rate',
  notes: 'notes', หมายเหตุ: 'notes',
  source_url: 'source_url', ที่มา: 'source_url', source: 'source_url',
  last_verified: 'last_verified', ตรวจล่าสุด: 'last_verified',
};
// CSV parser (รองรับ field มี comma ในเครื่องหมายคำพูด) — เหมือนหน้าสินค้า
function parseCsv(text) {
  const rows = [];
  let row = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') q = false;
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else if (c === '\r') { /* skip */ }
    else cell += c;
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter(r => r.some(x => x.trim()));
}
function fileToHsRecords(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map(h => {
    const key = h.trim().toLowerCase().replace(/\s+/g, '');
    return HS_COLMAP[key] ?? HS_COLMAP[h.trim()] ?? null;
  });
  return rows.slice(1).map(r => {
    const rec = {};
    headers.forEach((f, i) => { if (f && (r[i] ?? '').trim()) rec[f] = r[i].trim(); });
    // ตัวเลข: แปลงเป็น number; ถ้าไม่ใช่เลข ตัดทิ้ง (vat_rate NOT NULL default 7 — อย่าส่ง null)
    for (const k of ['duty_rate', 'vat_rate']) {
      if (rec[k] != null) { const n = Number(rec[k]); if (Number.isFinite(n)) rec[k] = n; else delete rec[k]; }
    }
    return rec;
  }).filter(r => r.hs_code);
}
const HS_TEMPLATE = 'hs_code,description_th,duty_rate,vat_rate,notes,source_url,last_verified\n' +
  '6306.22.00.000,เต็นท์ทำด้วยใยสังเคราะห์ (แคมป์ปิ้ง),30,7,,https://itd.customs.go.th,2026-07-17\n';

const money = (n) => n == null ? '—' : '฿' + Math.round(Number(n)).toLocaleString();
const num = (n) => n == null ? '—' : Number(n).toLocaleString();

function Kpi({ label, value, sub }) {
  return <div className="kpi"><div className="kpi-l">{label}</div><div className="kpi-v">{value}</div>{sub && <div className="kpi-s">{sub}</div>}</div>;
}

function Analytics() {
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [po, setPo] = useState([]);
  useEffect(() => { db.analytics().then(setA).catch(e => { if (isSchemaPending(e)) setPending(true); else setError(e.message ?? String(e)); }); }, []);
  useEffect(() => { db.poStats().then(setPo).catch(() => setPo([])); }, []);   // ใบสั่งซื้อ (deploy-28) — ไม่มีก็ข้ามไป ไม่ทำให้หน้าพัง
  if (pending) return <DbPending/>;
  if (error) return <div className="ferr">{error}</div>;
  if (!a) return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;

  const yr = new Date().getFullYear();
  const cur = a.yoy.find(r => r.yr === yr) ?? {};
  const prev = a.yoy.find(r => r.yr === yr - 1) ?? {};
  const delta = (c, p) => (p ? Math.round((c - p) / p * 100) : null);

  const monthsCur = a.byMonth.filter(m => m.yr === yr);
  const maxShip = Math.max(1, ...a.byMonth.map(m => Number(m.shipments)));

  if (!a.yoy.length && !a.byMonth.length) return (
    <div className="placeholder"><h2>ยังไม่มีข้อมูลสถิติ</h2>
      <p>เมื่อมี shipment ที่ของถึงแล้ว (event received) ตัวเลขจะขึ้นที่นี่</p></div>
  );

  return (
    <div className="analytics">
      <div className="kpis">
        <Kpi label="ชิปเมนต์ YTD" value={num(cur.shipments)}
          sub={delta(cur.shipments, prev.shipments) != null ? `${delta(cur.shipments, prev.shipments) > 0 ? '+' : ''}${delta(cur.shipments, prev.shipments)}% vs ปีก่อน` : null}/>
        <Kpi label="มูลค่านำเข้า" value={money(cur.import_value)}/>
        <Kpi label="อากร + VAT" value={money(cur.duty_vat)} sub="เพิ่ม = นำเข้าเยอะขึ้น"/>
        <Kpi label="lead time เฉลี่ย" value={cur.avg_lead_days != null ? `${cur.avg_lead_days} วัน` : '—'}/>
      </div>

      <div className="acard">
        <div className="acard-h">ชิปเมนต์รายเดือน (ปีนี้)</div>
        <div className="barchart">
          {Array.from({ length: 12 }, (_, i) => {
            const m = monthsCur.find(x => x.mo === i + 1);
            const v = m ? Number(m.shipments) : 0;
            return (
              <div className="bc-col" key={i}>
                <div className="bc-bar" style={{ height: `${v / maxShip * 100}%` }} title={`${v} ตู้`}/>
                <div className="bc-x">{i + 1}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="arow">
        <div className="acard">
          <div className="acard-h">สัดส่วนขนส่ง</div>
          {a.byMode.map(m => (
            <div className="brow" key={m.mode}>
              <span className={`bdot ${m.mode}`}/>
              <span className="bname">{m.mode === 'air' ? 'ทางอากาศ' : 'ทางเรือ'}</span>
              <span className="bval">{num(m.shipments)} ตู้ · {money(m.import_value)}</span>
            </div>
          ))}
          {!a.byMode.length && <div className="jr-empty">—</div>}
        </div>
        <div className="acard">
          <div className="acard-h">แยกตาม supplier</div>
          {a.bySupplier.slice(0, 8).map((s, i) => (
            <div className="brow" key={i}>
              <span className="bname">{s.supplier_name}</span>
              <span className="bval">{num(s.consignments)} ครั้ง · {money(s.import_value)}</span>
            </div>
          ))}
          {!a.bySupplier.length && <div className="jr-empty">—</div>}
        </div>
      </div>

      {po.length > 0 && (() => {
        const live = po.filter(p => p.status !== 'cancelled');
        const open_ = live.filter(p => p.status === 'open');
        const left = open_.reduce((t, p) => t + (Number(p.pieces_left) || 0), 0);
        const qtyOff = live.reduce((t, p) => t + (Number(p.qty_off) || 0), 0);
        const priceOff = live.reduce((t, p) => t + (Number(p.price_off) || 0), 0);
        const today = new Date().toISOString().slice(0, 10);
        const late = open_.filter(p => p.expected_ship && p.expected_ship < today && Number(p.pieces_left) > 0);
        const byBrand = {};
        for (const p of open_) {
          const b = (byBrand[p.brand] ??= { brand: p.brand, pos: 0, left: 0, off: 0 });
          b.pos++; b.left += Number(p.pieces_left) || 0;
          b.off += (Number(p.qty_off) || 0) + (Number(p.price_off) || 0);
        }
        const rank = Object.values(byBrand).sort((x, y) => y.left - x.left).slice(0, 8);
        return (
          <div className="acard">
            <div className="acard-h">ใบสั่งซื้อที่ยังรอของ · ของที่มาถึงตรงกับที่สั่งไม๊</div>
            <div className="kpis" style={{ marginBottom: 12 }}>
              <Kpi label="ใบที่ยังรอของ" value={num(open_.length)} sub={`ทั้งหมด ${num(live.length)} ใบ`}/>
              <Kpi label="ยังไม่มาอีก" value={num(left)} sub="ชิ้น (รวมทุกแบรนด์)"/>
              <Kpi label="เลยวันที่นัดส่ง" value={num(late.length)}
                sub={late.length ? late.slice(0, 3).map(p => p.brand).join(', ') : 'ไม่มีใบไหนเลยนัด'}/>
              <Kpi label="ที่มาถึงไม่ตรงกับที่สั่ง" value={num(qtyOff + priceOff)}
                sub={`จำนวน ${num(qtyOff)} · ราคา ${num(priceOff)} บรรทัด`}/>
            </div>
            {rank.map(b => (
              <div className="brow" key={b.brand}>
                <span className="bname">{b.brand}</span>
                <span className="bval">{num(b.pos)} ใบ · ยังไม่มา {num(b.left)} ชิ้น{b.off ? ` · ไม่ตรง ${num(b.off)}` : ''}</span>
              </div>
            ))}
            <div className="kpi-s" style={{ marginTop: 8 }}>
              เทียบที่ราคาต่อชิ้นเสมอ (แปลงกล่อง→ชิ้นให้แล้ว) · รายละเอียดรายบรรทัดดูที่แท็บ “แบรนด์”
            </div>
          </div>
        );
      })()}

      <div className="arow">
        <div className="acard insight">
          <div className="acard-h">FTA savings (ประหยัดอากร)</div>
          <div className="kpi-v">{money(a.ftaSavings.find(x => x.yr === yr)?.duty_saved)}</div>
          <div className="kpi-s">ประมาณจากส่วนต่าง MFN − อากรจริงที่ใช้สิทธิ (สูตร proxy)</div>
        </div>
        <div className="acard insight">
          <div className="acard-h">Demurrage / จับ field ไม่ตรง</div>
          <div className="kpi-v">{num(cur.demurrage_count ?? 0)} ครั้ง</div>
          <div className="kpi-s">เอกสาร flagged ปีนี้: {num(a.docQuality.find(x => x.yr === yr)?.flagged_docs ?? 0)} ใบ (มูลค่าเชิงป้องกัน)</div>
        </div>
      </div>
    </div>
  );
}

// เพิ่มพนักงานใหม่ = สร้างบัญชี + ให้สิทธิ์ในครั้งเดียว (ผ่าน /api/users — กุญแจอยู่ฝั่งเซิร์ฟเวอร์)
function NewUserModal({ onClose, onDone }) {
  const [f, setF] = useState({ full_name: '', email: '', password: '', role: 'coordinator' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const [copied, setCopied] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // สุ่มรหัสตั้งต้นที่อ่านออก/บอกต่อทางโทรศัพท์ได้ (ตัดตัวที่สับสน 0/O/1/l/I ออก)
  const roll = () => {
    const A = 'ABCDEFGHJKMNPQRSTUVWXYZ', a = 'abcdefghijkmnpqrstuvwxyz', n = '23456789';
    const pick = (s, k) => Array.from({ length: k }, (_, i) => s[(crypto.getRandomValues(new Uint32Array(1))[0]) % s.length]).join('');
    setF({ ...f, password: `${pick(A, 2)}${pick(a, 4)}${pick(n, 3)}` });
  };

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await db.createAppUser({ fullName: f.full_name.trim(), email: f.email.trim(), password: f.password, role: f.role });
      setDone({ email: f.email.trim(), password: f.password, name: f.full_name.trim(), role: f.role });
      onDone?.();
    } catch (e) { setErr(e.message ?? String(e)); }
    setBusy(false);
  };

  if (done) return (
    <Modal title="เพิ่มพนักงานแล้ว" sub="บัญชีใช้งานได้ทันที ไม่ต้องยืนยันอีเมล" onClose={onClose}>
      <div className="pp-done">
        <div className="pp-done-t">{done.name} · {db.roleLabel(done.role)}</div>
        <div className="pp-done-s">ส่งสองบรรทัดนี้ให้เจ้าตัว แล้วบอกให้เปลี่ยนรหัสผ่านเองหลังเข้าครั้งแรก
          (หน้าเข้าสู่ระบบ → “ลืมรหัสผ่าน”)</div>
        <pre className="pp-cred">อีเมล: {done.email}{'\n'}รหัสผ่าน: {done.password}</pre>
        <button className="btn2 sm" onClick={() => {
          navigator.clipboard?.writeText(`อีเมล: ${done.email}\nรหัสผ่าน: ${done.password}`)
            .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); }).catch(() => {});
        }}>{copied ? '✓ คัดลอกแล้ว' : 'คัดลอก'}</button>
      </div>
      <div className="mfoot"><button className="btn1" onClick={onClose}>เสร็จแล้ว</button></div>
    </Modal>
  );

  return (
    <Modal title="เพิ่มพนักงานใหม่" sub="สร้างบัญชีเข้าระบบ + ให้สิทธิ์ INBOUNDpoint ในครั้งเดียว" onClose={onClose}>
      {err && <div className="ferr">{err}</div>}
      <Fld label="ชื่อ–นามสกุล" hint="ชื่อที่จะขึ้นในรายการมอบหมายงาน">
        <input value={f.full_name} onChange={set('full_name')} placeholder="วุฒิชัย เกียรติทนงศักดิ์"/></Fld>
      <Fld label="อีเมลที่ใช้เข้าระบบ" hint="ถ้าเป็นโดเมนที่ไม่มีกล่องจดหมายจริง จะกด “ลืมรหัสผ่าน” ไม่ได้ ต้องให้แอดมินตั้งใหม่ให้">
        <input type="email" value={f.email} onChange={set('email')} placeholder="name@campstudio.co.th"/></Fld>
      <Fld label="รหัสผ่านตั้งต้น" hint="อย่างน้อย 8 ตัว · บอกเจ้าตัวแล้วให้เขาเปลี่ยนเองหลังเข้าครั้งแรก">
        <div className="pp-pwrow">
          <input value={f.password} onChange={set('password')} placeholder="อย่างน้อย 8 ตัว"/>
          <button type="button" className="btn2 sm" onClick={roll}>สุ่มให้</button>
        </div></Fld>
      <Fld label="ให้สิทธิ์อะไร" hint={db.APP_ROLES.find(r => r.k === f.role)?.d}>
        <select value={f.role} onChange={set('role')}>
          {db.APP_ROLES.map(r => <option key={r.k} value={r.k}>{r.t}</option>)}
        </select></Fld>
      <div className="mfoot">
        <button className="btn2" onClick={onClose}>ยกเลิก</button>
        <button className="btn1" disabled={busy} onClick={submit}>
          {busy ? 'กำลังสร้าง…' : 'สร้างบัญชี + ให้สิทธิ์'}</button>
      </div>
    </Modal>
  );
}

// ── ถังขยะ — ของที่ถูกลบ กู้คืนได้ (deploy-33) ──────────────────────────────
function Trash() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState({});
  const [showDone, setShowDone] = useState(false);
  const [needDeploy, setNeedDeploy] = useState(false);

  const load = async () => {
    try { setList(await db.listTrash()); setErr(null); setNeedDeploy(false); }
    catch (e) {
      const miss = e?.code === 'PGRST205' || e?.code === '42P01'
        || /ib_trash|does not exist|Could not find the table/i.test(e?.message ?? String(e));
      if (miss) { setNeedDeploy(true); setErr(null); } else setErr(e.message ?? String(e));
    }
  };
  useEffect(() => { load(); }, []);
  const say = (m) => { setMsg(m); setTimeout(() => setMsg(null), 6000); };

  const act = async (fn, key) => {
    setBusy(key); setErr(null);
    try { say(await fn()); await load(); }
    catch (e) { setErr(e.message ?? String(e)); }
    setBusy(null);
  };

  if (needDeploy) return (
    <div className="placeholder">
      <h2>ถังขยะยังไม่พร้อมใช้</h2>
      <p>ต้องรัน <code>db/deploy-33-trash.sql</code> บนฐานข้อมูลก่อน</p>
      <button className="tab" onClick={load}>ลองใหม่</button>
    </div>
  );
  if (err && !list) return <div className="ferr">{err}</div>;
  if (!list) return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;

  // ลบครั้งเดียว = หลายแถว → จับเป็นก้อนด้วย txid แล้วโชว์เป็นชุดเดียว
  const groups = [];
  const byTx = {};
  for (const t of list) {
    if (!byTx[t.txid]) { byTx[t.txid] = { txid: t.txid, rows: [], at: t.deleted_at, who: t.deleted_by, db_user: t.db_user }; groups.push(byTx[t.txid]); }
    byTx[t.txid].rows.push(t);
  }
  const shown = groups.filter(g => showDone || g.rows.some(r => !r.restored_at));
  const openN = list.filter(t => !t.restored_at).length;
  const fmt = (iso) => new Date(iso).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="trash">
      <div className="pp-lead">
        ทุกอย่างที่ถูกลบจะมาอยู่ที่นี่ ไม่ได้หายไปไหน · กู้กลับได้ทั้งชุดหรือทีละรายการ
        <br/>ลบครั้งเดียวมักลากของไปหลายสิบรายการ — <b>กู้ทั้งชุด</b> จะผูกทุกอย่างกลับให้เหมือนเดิม
      </div>
      {err && <div className="ferr">{err}</div>}
      {msg && <div className="pp-ok">{msg}</div>}

      <div className="pp-bar">
        <span className="pp-cnt">ยังไม่ได้กู้ <b>{openN}</b> รายการ</span>
        <span className="pp-cnt">ทั้งหมด <b>{list.length}</b></span>
        <label className="tr-sw">
          <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)}/>
          <span>แสดงชุดที่กู้คืนไปแล้วด้วย</span>
        </label>
        <button className="mini" onClick={load}>↻ โหลดใหม่</button>
      </div>

      {!shown.length && (
        <div className="bh-empty">
          {list.length ? 'กู้คืนไปหมดแล้ว — ติ๊ก “แสดงชุดที่กู้คืนไปแล้ว” เพื่อดูย้อนหลัง'
                       : 'ยังไม่มีอะไรถูกลบ — ถ้ามีใครลบอะไร จะมาโผล่ที่นี่ทันที'}
        </div>
      )}

      {shown.map(g => {
        const left = g.rows.filter(r => !r.restored_at);
        const on = !!open[g.txid];
        const kinds = {};
        for (const r of g.rows) kinds[r.table_name] = (kinds[r.table_name] ?? 0) + 1;
        return (
          <div className={`tr-card ${left.length ? '' : 'done'}`} key={g.txid}>
            <button className="tr-h" onClick={() => setOpen(o => ({ ...o, [g.txid]: !on }))}>
              <span className="tr-dot"/>
              <span className="tr-when">{fmt(g.at)}</span>
              <span className="tr-what">
                {Object.entries(kinds).map(([t, n]) => `${db.TRASH_LABEL[t] ?? t} ${n}`).join(' · ')}
              </span>
              <span className="tr-who">{g.who ? 'ลบจากในแอพ' : `ลบจากหลังบ้าน (${g.db_user})`}</span>
              <span className="tr-tag">{left.length ? `${left.length} รายการ` : '✓ กู้คืนแล้ว'}</span>
              <span className="po-cv">{on ? '▲' : '▼'}</span>
            </button>
            {on && (
              <div className="tr-body">
                {g.rows.map(r => (
                  <div className={`tr-row ${r.restored_at ? 'back' : ''}`} key={r.id}>
                    <span className="tr-kind">{db.TRASH_LABEL[r.table_name] ?? r.table_name}</span>
                    <span className="tr-desc">{db.describeTrashRow(r)}</span>
                    {r.restored_at
                      ? <span className="tr-ok">✓ กู้แล้ว</span>
                      : <button className="mini" disabled={busy === r.id}
                          onClick={() => act(() => db.restoreTrash(r.id), r.id)}>กู้รายการนี้</button>}
                  </div>
                ))}
                {left.length > 0 && (
                  <div className="tr-foot">
                    <button className="btn1 sm" disabled={busy === g.txid}
                      onClick={() => act(() => db.restoreTrashBatch(g.txid), g.txid)}>
                      {busy === g.txid ? 'กำลังกู้คืน…' : `กู้ทั้งชุด (${left.length} รายการ)`}
                    </button>
                    <span className="fhint">แนะนำกู้ทั้งชุด — กู้ทีละรายการอาจติดเพราะของที่มันผูกอยู่ยังไม่กลับมา</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── ผู้ใช้ & สิทธิ์ (deploy-29) — ไม่มีแถวใน inbound.app_role = เข้าแอพนี้ไม่ได้ ──
// สุ่มรหัสตั้งต้นที่อ่านออก/บอกต่อทางโทรศัพท์ได้ (ตัดตัวที่สับสน 0/O/1/l/I ออก)
function rollPassword() {
  const A = 'ABCDEFGHJKMNPQRSTUVWXYZ', a = 'abcdefghijkmnpqrstuvwxyz', n = '23456789';
  const pick = (src, k) => Array.from({ length: k },
    () => src[crypto.getRandomValues(new Uint32Array(1))[0] % src.length]).join('');
  return `${pick(A, 2)}${pick(a, 4)}${pick(n, 3)}`;
}

// ตั้งรหัสผ่านใหม่ให้พนักงาน
// ⚠ คำเตือนต้องพูดความจริง: บัญชี CAMP STUDIO เป็นถังเดียวทุกแอพ รหัสใหม่จึงใช้ได้ทุกแอพ
//   และ ban/รหัสใหม่ **ไม่ตัดเซสชันที่เปิดค้างอยู่** จนกว่าโทเคนจะหมดอายุ — ห้ามเขียนว่า "ทันที"
function ResetPwModal({ u, onClose, onDone }) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await db.resetUserPassword(u.user_id, pw); setDone(true); onDone?.(); }
    catch (e) { setErr(e.message ?? String(e)); }
    setBusy(false);
  };

  if (done) return (
    <Modal title="ตั้งรหัสผ่านใหม่แล้ว" sub="ส่งข้อความข้างล่างให้เจ้าตัว" onClose={onClose}>
      <div className="pp-done">
        <div className="pp-done-t">{u.name}</div>
        <div className="pp-done-s">ครั้งแรกที่เข้า ระบบจะให้เจ้าตัวตั้งรหัสของตัวเองทันที
          รหัสข้างล่างจึงใช้ได้ครั้งเดียว</div>
        <pre className="pp-cred">อีเมล: {u.email}{'\n'}รหัสผ่าน: {pw}</pre>
        <button className="btn2 sm" onClick={() => {
          navigator.clipboard?.writeText(`อีเมล: ${u.email}\nรหัสผ่าน: ${pw}`)
            .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); }).catch(() => {});
        }}>{copied ? '✓ คัดลอกแล้ว' : 'คัดลอก'}</button>
      </div>
      <div className="mfoot"><button className="btn1" onClick={onClose}>เสร็จแล้ว</button></div>
    </Modal>
  );

  return (
    <Modal title={`ตั้งรหัสผ่านใหม่ให้ ${u.name}`}
      sub="เจ้าตัวจะเข้าด้วยรหัสเดิมไม่ได้อีก" onClose={onClose}>
      <div className="pp-allapps">
        <b>รหัสนี้ใช้กับทุกแอพของบริษัท</b>
        บัญชีเข้าระบบเป็นบัญชีเดียวกันทั้ง INBOUNDpoint · ขาย · บัญชี · คลัง · แพ็ค · สั่งซื้อ
        — เปลี่ยนที่นี่แล้วเปลี่ยนทุกที่ · ถ้าตอนนี้เขาเปิดแอพค้างอยู่ จะใช้ต่อได้อีกพักหนึ่งจนหมดเวลาเซสชัน
      </div>
      {err && <div className="ferr">{err}</div>}
      <Fld label="รหัสผ่านใหม่" hint={`อย่างน้อย ${db.MIN_PW} ตัว · บอกเจ้าตัวแล้วเขาจะตั้งของตัวเองตอนเข้าครั้งแรก`}>
        <div className="pp-pwrow">
          <input value={pw} onChange={e => setPw(e.target.value)} placeholder={`อย่างน้อย ${db.MIN_PW} ตัว`}/>
          <button type="button" className="btn2 sm" onClick={() => setPw(rollPassword())}>สุ่มให้</button>
        </div></Fld>
      <div className="mfoot">
        <button className="btn2" onClick={onClose}>ไม่เปลี่ยน</button>
        <button className="btn1" disabled={busy || pw.length < db.MIN_PW} onClick={submit}>
          {busy ? 'กำลังตั้ง…' : 'ตั้งรหัสผ่านใหม่'}</button>
      </div>
    </Modal>
  );
}

// เปลี่ยนอีเมลที่ใช้เข้าระบบ = เปลี่ยน "ชื่อผู้ใช้" — พิมพ์ซ้ำเพื่อยืนยัน เพราะพิมพ์ผิดแล้วเจ้าตัวเข้าไม่ได้เลย
function SetEmailModal({ u, onClose, onDone }) {
  const [email, setEmail] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ok = email.trim() && email.trim().toLowerCase() === again.trim().toLowerCase()
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await db.setUserEmail(u.user_id, email.trim()); onDone?.(); onClose(); }
    catch (e) { setErr(e.message ?? String(e)); }
    setBusy(false);
  };

  return (
    <Modal title={`เปลี่ยนอีเมลเข้าระบบของ ${u.name}`}
      sub={`ตอนนี้ใช้ ${u.email} เข้าระบบอยู่`} onClose={onClose}>
      <div className="pp-allapps">
        <b>อีเมลนี้คือชื่อผู้ใช้ของทุกแอพ</b>
        เปลี่ยนแล้วเจ้าตัวต้องใช้อีเมลใหม่เข้าทุกแอพของบริษัท · รหัสผ่านเดิมยังใช้ได้
        · งานเก่าทั้งหมดยังผูกกับคนเดิมครบ ไม่หายไปไหน
      </div>
      {err && <div className="ferr">{err}</div>}
      <Fld label="อีเมลใหม่"><input type="email" value={email}
        onChange={e => setEmail(e.target.value)} placeholder="name@campstudio.co.th"/></Fld>
      <Fld label="พิมพ์อีเมลใหม่อีกครั้ง" hint="กันพิมพ์ผิด — พิมพ์ผิดแล้วเจ้าตัวเข้าระบบไม่ได้เลย">
        <input type="email" value={again} onChange={e => setAgain(e.target.value)}/></Fld>
      <div className="mfoot">
        <button className="btn2" onClick={onClose}>ไม่เปลี่ยน</button>
        <button className="btn1" disabled={busy || !ok} onClick={submit}>
          {busy ? 'กำลังเปลี่ยน…' : 'เปลี่ยนอีเมล'}</button>
      </div>
    </Modal>
  );
}

// ประวัติการจัดการบัญชี — append-only ในฐานข้อมูล แก้/ลบไม่ได้แม้แต่แอดมิน
function AccountLog() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    db.listAdminEvents(200).then(setList)
      .catch(e => setErr(/42501|PGRST202|does not exist|Could not find/i.test(e?.message ?? '')
        ? 'ต้องรัน db/deploy-35-account-admin.sql ก่อน' : (e.message ?? String(e))));
  }, []);
  const fmt = (t) => new Date(t).toLocaleString('th-TH',
    { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  if (err) return <div className="ae-empty">{err}</div>;
  if (!list) return <div className="ae-empty">กำลังโหลด…</div>;
  if (!list.length) return <div className="ae-empty">ยังไม่มีรายการ</div>;
  return (
    <div className="ae">
      {list.map(e => (
        <div className="ae-row" key={e.id}>
          <span className="ae-at">{fmt(e.at)}</span>
          <span className={`ae-act a-${e.action}`}>{db.ADMIN_ACTION_LABEL[e.action] ?? e.action}</span>
          <span className="ae-who">{e.target_email ?? '—'}</span>
          <span className="ae-by">โดย {e.actor_email ?? (e.db_user === 'service_role' ? 'ระบบ' : e.db_user)}</span>
          {e.detail?.from && e.detail?.to && <span className="ae-d">{e.detail.from} → {e.detail.to}</span>}
          {!e.detail?.from && e.detail?.to && <span className="ae-d">{db.roleLabel(e.detail.to)}</span>}
        </div>
      ))}
    </div>
  );
}

function People() {
  const { session } = useAuth();
  const myId = session?.user?.id ?? null;
  const [srv, setSrv] = useState(null);        // ฝั่งเซิร์ฟเวอร์พร้อมหรือยัง (ถามตั้งแต่โหลดหน้า)
  const [pwFor, setPwFor] = useState(null);    // แถวที่กำลังตั้งรหัสใหม่
  const [emFor, setEmFor] = useState(null);    // แถวที่กำลังเปลี่ยนอีเมล
  const [selfWarn, setSelfWarn] = useState(null); // {u, role} ยืนยันก่อนเปลี่ยนสิทธิ์ของตัวเอง
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(null);      // user_id ที่กำลังบันทึก
  const [confirm, setConfirm] = useState(null); // แถวที่กำลังยืนยันถอดสิทธิ์
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  const [needDeploy, setNeedDeploy] = useState(false);
  const load = async () => {
    try { setList(await db.listAppUsers()); setErr(null); setNeedDeploy(false); }
    catch (e) {
      // ฟังก์ชันยังไม่มี = ยังไม่ได้รัน db/deploy-32 — บอกตรง ๆ ดีกว่าโยน error ดิบใส่หน้าจอ
      // (PGRST202 = ไม่พบฟังก์ชัน · PGRST205/42P01 = ไม่พบตาราง/view สมัย deploy-29)
      const m = e?.code === 'PGRST202' || e?.code === 'PGRST205' || e?.code === '42P01'
        || /ib_list_users|list_users|does not exist|Could not find the (table|function)/i.test(e?.message ?? String(e));
      if (m) { setNeedDeploy(true); setErr(null); } else setErr(e.message ?? String(e));
    }
  };
  useEffect(() => { load(); db.serverStatus().then(setSrv).catch(() => setSrv({ configured: false })); }, []);

  const say = (m) => { setMsg(m); setTimeout(() => setMsg(null), 4500); };
  const doGrant = async (u, role) => {
    setBusy(u.user_id); setErr(null);
    try { await db.setAppRole(u.user_id, role); await load(); say(`${u.name} เป็น “${db.roleLabel(role)}” แล้ว — ครั้งต่อไปที่เข้าระบบจะเห็นสิทธิ์ใหม่`); }
    catch (e) {
      // ด่านกันล็อกตัวเองออกใน DB (deploy-35) ส่งข้อความไทยมาแล้ว — โชว์ตรง ๆ
      setErr(e.message ?? String(e));
    }
    setBusy(null);
  };
  // เปลี่ยนสิทธิ์ของ "ตัวเอง" ต้องยืนยันก่อน — ลดสิทธิ์ตัวเองคือทางที่ทำให้เข้าหน้านี้ไม่ได้อีก
  const grant = (u, role) => {
    if (u.user_id === myId && role !== 'admin') { setSelfWarn({ u, role }); return; }
    doGrant(u, role);
  };
  const revoke = async (u) => {
    setConfirm(null); setBusy(u.user_id); setErr(null);
    try { await db.removeAppRole(u.user_id); await load(); say(`ถอดสิทธิ์ ${u.name} แล้ว — เข้า INBOUNDpoint ไม่ได้ทันที (บัญชีและแอพอื่นไม่กระทบ)`); }
    catch (e) { setErr(e.message ?? String(e)); }
    setBusy(null);
  };

  if (needDeploy) return (
    <div className="placeholder">
      <h2>หน้านี้ยังไม่พร้อมใช้</h2>
      <p>ต้องรัน <code>db/deploy-32-user-admin-rpc.sql</code> บนฐานข้อมูลก่อน แล้วกลับมาโหลดหน้านี้ใหม่</p>
      <button className="tab" onClick={load}>ลองใหม่</button>
    </div>
  );
  if (err && !list) return <div className="ferr">{err}</div>;
  if (!list) return <div className="placeholder"><h2>กำลังโหลด…</h2></div>;

  const hit = (u) => !q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase());
  const has = list.filter(u => u.app_role).filter(hit);
  const none = list.filter(u => !u.app_role).filter(hit);
  const admins = list.filter(u => u.app_role === 'admin').length;
  const fmtSeen = (t) => t ? new Date(t).toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: '2-digit' }) : 'ไม่เคยเข้า';

  // deploy-35 ยังไม่ได้รัน = ไม่มีคอลัมน์ managed_here เลย (ไม่ใช่ว่าเป็น false)
  //   → ต้องเช็คการ "มีคีย์" ไม่ใช่ค่าความจริง ไม่งั้นจะโชว์ปุ่มที่กดแล้วพัง
  const hasD35 = !!list.length && 'managed_here' in list[0];
  const canManage = (u) => hasD35 && u.managed_here === true && u.app_role && u.app_role !== 'admin';
  // 'มีกุญแจ' ไม่พอ ต้อง 'กุญแจใช้ได้จริง' (เซิร์ฟเวอร์ยิงทดสอบให้แล้ว)
  // `working` undefined = Function รุ่นเก่าที่ยังไม่มีตัวตรวจ → ถอยไปใช้ configured
  const srvReady = srv ? (srv.working ?? srv.configured) === true : false;

  const Row = ({ u, on }) => {
    const isMe = u.user_id === myId;
    const lastAdmin = u.app_role === 'admin' && admins <= 1;
    return (
      <div className={`pp-row ${on ? 'on' : ''} ${isMe ? 'me' : ''}`}>
        <span className="pp-nm">{u.name}{isMe && <i className="pp-you">คุณ</i>}</span>
        <span className="pp-em">{u.email}</span>
        <span className="pp-seen">{fmtSeen(u.last_sign_in_at)}</span>
        <span className="pp-act">{u.activity > 0 ? `เคยทำงานไว้ ${u.activity} รายการ` : ''}</span>
        <select className="pp-sel" disabled={busy === u.user_id || lastAdmin}
          title={lastAdmin ? 'เหลือแอดมินคนเดียว — ตั้งแอดมินคนอื่นก่อนถึงจะเปลี่ยนได้' : undefined}
          value={u.app_role ?? ''} onChange={e => grant(u, e.target.value)}>
          <option value="" disabled>{on ? '—' : 'เลือกสิทธิ์เพื่อเปิดให้เข้า…'}</option>
          {db.APP_ROLES.map(r => <option key={r.k} value={r.k}>{r.t}</option>)}
        </select>
        {on && (lastAdmin
          ? <span className="pp-lock" title="ถ้าถอดคนนี้จะไม่เหลือแอดมินเลย — ตั้งแอดมินคนอื่นก่อน">แอดมินคนสุดท้าย</span>
          : <button className="pp-x" disabled={busy === u.user_id} onClick={() => setConfirm(u)}>ถอดสิทธิ์</button>)}
        {on && canManage(u) && (
          <span className="pp-acct">
            <button className="pp-mini" disabled={!srvReady}
              title={srvReady ? 'ตั้งรหัสผ่านใหม่ให้คนนี้' : 'ยังตั้งกุญแจฝั่งเซิร์ฟเวอร์ไม่เสร็จ'}
              onClick={() => setPwFor(u)}>ตั้งรหัสผ่านใหม่</button>
            <button className="pp-mini" disabled={!srvReady}
              title={srvReady ? 'เปลี่ยนอีเมลที่ใช้เข้าระบบ' : 'ยังตั้งกุญแจฝั่งเซิร์ฟเวอร์ไม่เสร็จ'}
              onClick={() => setEmFor(u)}>เปลี่ยนอีเมล</button>
          </span>
        )}
        {on && hasD35 && u.app_role === 'admin' && (
          <span className="pp-note-sm">บัญชีแอดมินคุมหลายแอพ — ให้เจ้าตัวเปลี่ยนรหัสเอง</span>
        )}
        {on && hasD35 && u.app_role && u.app_role !== 'admin' && !u.managed_here && (
          <span className="pp-note-sm">บัญชีนี้สร้างจากแอพอื่น — ตั้งรหัสผ่านจากที่นี่ไม่ได้</span>
        )}
      </div>
    );
  };

  return (
    <div className="people">
      <div className="pp-lead">
        ใครมีแถวในนี้เท่านั้นที่เข้า INBOUNDpoint ได้ · การถอดสิทธิ์ที่นี่ไม่ลบบัญชีและไม่กระทบแอพอื่น
      </div>
      {srv && !srvReady && (
        <div className="pp-setup">
          <div className="pp-setup-t">
            {srv.configured ? 'กุญแจที่ตั้งไว้ใช้งานไม่ได้' : 'ยังตั้งกุญแจฝั่งเซิร์ฟเวอร์ไม่เสร็จ'}
          </div>
          <div className="pp-setup-s">
            เปิด/ถอดสิทธิ์ ใช้ได้ตามปกติ — แต่ <b>เพิ่มพนักงานใหม่</b> · <b>ตั้งรหัสผ่านใหม่</b> ·
            <b> เปลี่ยนอีเมล</b> ยังทำไม่ได้ เพราะสามอย่างนี้ต้องใช้กุญแจระดับผู้ดูแลฐานข้อมูล
            ซึ่งเก็บไว้ฝั่งเซิร์ฟเวอร์เท่านั้น ไม่เคยอยู่ในหน้าเว็บ
          </div>
          {srv.configured && srv.probe && (
            <div className="pp-setup-s">
              ผลตรวจ: ระบบบัญชีผู้ใช้ {srv.probe.auth_admin ? '✓ ผ่าน' : '✕ ไม่ผ่าน'} ·
              {' '}ฐานข้อมูล {srv.probe.db_app_role ? '✓ ผ่าน' : '✕ ไม่ผ่าน'}
              {srv.probe.detail && <> · {srv.probe.detail}</>}
              <br/>มักเกิดจากวาง<b>คีย์ anon</b>มาแทน (คนละตัว) หรือวางไม่ครบ — ขอให้ช่วยคัดลอกใหม่จากแถว
              {' '}<b>service_role</b> แล้วตั้งอีกครั้ง
            </div>
          )}
          <div className="pp-setup-s">ค่ากุญแจอยู่ที่ {srv.where ?? 'Supabase → Project Settings → API Keys → service_role'}
            {' '}แล้วรันคำสั่งนี้ในเครื่องที่ deploy แอพ:</div>
          <pre className="pp-setup-c">{srv.setup ?? 'npx wrangler pages secret put SUPABASE_SERVICE_ROLE_KEY --project-name campstudio-inboundpoint'}</pre>
          <button className="btn2 sm" onClick={() => db.serverStatus().then(setSrv)}>ตั้งแล้ว — ตรวจอีกครั้ง</button>
        </div>
      )}
      <div className="pp-guide">
        <div className="pp-guide-t">พนักงานใหม่ที่ยังไม่มีบัญชี</div>
        <div className="pp-guide-s">สร้างบัญชีเข้าระบบและให้สิทธิ์ได้จบที่นี่เลย ไม่ต้องไปแอพอื่น</div>
        <button className="btn1 sm" disabled={srv ? !srvReady : false}
          title={srv && !srvReady ? 'กุญแจฝั่งเซิร์ฟเวอร์ยังใช้งานไม่ได้' : undefined}
          onClick={() => setAdding(true)}>＋ เพิ่มพนักงานใหม่</button>
      </div>
      {err && <div className="ferr">{err}</div>}
      {msg && <div className="pp-ok">{msg}</div>}

      <div className="pp-bar">
        <span className="pp-cnt">เข้าได้ <b>{list.filter(u => u.app_role).length}</b> คน</span>
        <span className="pp-cnt">แอดมิน <b>{admins}</b></span>
        <input className="pp-q" value={q} onChange={e => setQ(e.target.value)} placeholder="ค้นหาชื่อ หรือ อีเมล"/>
      </div>

      <div className="pp-lg">
        {db.APP_ROLES.map(r => (
          <div className="pp-lg-r" key={r.k}><b>{r.t}</b><span>{r.d}</span></div>
        ))}
      </div>

      <div className="pp-h">เข้าแอพนี้ได้ ({has.length})</div>
      {has.map(u => <Row key={u.user_id} u={u} on/>)}
      {!has.length && <div className="jr-empty">ไม่พบ</div>}

      <div className="pp-h">ยังไม่มีสิทธิ์ ({none.length}) — เลือกสิทธิ์ในช่องขวาเพื่อเปิดให้เข้า</div>
      {none.map(u => <Row key={u.user_id} u={u}/>)}
      {!none.length && <div className="jr-empty">ไม่พบ</div>}

      <div className="pp-h">ประวัติการจัดการบัญชี</div>
      <div className="pp-lead">บันทึกอัตโนมัติ แก้หรือลบไม่ได้ — ใครให้สิทธิ์ใคร ใครตั้งรหัสให้ใคร เมื่อไร</div>
      <AccountLog/>

      {adding && <NewUserModal onClose={() => setAdding(false)} onDone={load}/>}
      {pwFor && <ResetPwModal u={pwFor} onClose={() => setPwFor(null)} onDone={load}/>}
      {emFor && <SetEmailModal u={emFor} onClose={() => setEmFor(null)} onDone={load}/>}

      {selfWarn && (
        <Modal title="เปลี่ยนสิทธิ์ของตัวเอง?"
          sub="ลดสิทธิ์ตัวเองแล้วจะเข้าหน้านี้ไม่ได้อีก และเปิดคืนให้ตัวเองไม่ได้"
          onClose={() => setSelfWarn(null)}>
          <p>คุณกำลังเปลี่ยนสิทธิ์ของตัวเองจาก <b>{db.roleLabel(selfWarn.u.app_role)}</b> เป็น{' '}
            <b>{db.roleLabel(selfWarn.role)}</b></p>
          <p className="pp-warn">ถ้าทำแล้ว ต้องให้แอดมินอีกคนเปิดสิทธิ์คืนให้
            (ตอนนี้มีแอดมิน {admins} คน)</p>
          <div className="mfoot">
            <button className="btn2" onClick={() => setSelfWarn(null)}>ไม่เปลี่ยน</button>
            <button className="btn-del" onClick={() => { const w = selfWarn; setSelfWarn(null); doGrant(w.u, w.role); }}>
              เปลี่ยนสิทธิ์ของตัวเอง</button>
          </div>
        </Modal>
      )}

      {confirm && (
        <Modal title={`ถอดสิทธิ์ ${confirm.name}?`}
          sub="เข้า INBOUNDpoint ไม่ได้ทันที · บัญชี Supabase และสิทธิ์ในแอพอื่นไม่กระทบ · เปิดคืนได้ทุกเมื่อ"
          onClose={() => setConfirm(null)}>
          <p>{confirm.email} · สิทธิ์ตอนนี้ {db.roleLabel(confirm.app_role)}</p>
          {confirm.activity > 0 && (
            <p className="pp-warn">คนนี้เคยทำงานไว้ {confirm.activity} รายการ — งานเก่ายังอยู่ครบ แต่จะมอบหมายงานใหม่ให้ไม่ได้</p>
          )}
          <div className="mfoot">
            <button className="btn2" onClick={() => setConfirm(null)}>ไม่ถอด</button>
            <button className="btn-del" onClick={() => revoke(confirm)}>ถอดสิทธิ์</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Settings: hs_rule CRUD + fta + audit ───────────────────────────────────────
function HsRuleEditor({ rule, onClose, onSaved }) {
  const isNew = !rule;
  const [f, setF] = useState(rule ?? { hs_code: '', description_th: '', duty_rate: '', vat_rate: 7, measures: '[]', fta_notes: '', notes: '', source_url: '', last_verified: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      let measures;
      try { measures = typeof f.measures === 'string' ? JSON.parse(f.measures || '[]') : (f.measures ?? []); }
      catch { throw new Error('measures ต้องเป็น JSON array ที่ถูกต้อง'); }
      await db.saveHsRule({
        hs_code: f.hs_code.trim(),
        description_th: f.description_th || null,
        duty_rate: f.duty_rate === '' ? null : Number(f.duty_rate),
        vat_rate: f.vat_rate === '' ? 7 : Number(f.vat_rate),
        measures, fta_notes: f.fta_notes || null, notes: f.notes || null,
        source_url: f.source_url || null,
        last_verified: f.last_verified || null,
      }, isNew);
      onSaved(); onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };
  const measuresText = typeof f.measures === 'string' ? f.measures : JSON.stringify(f.measures, null, 1);
  return (
    <Modal title={isNew ? 'เพิ่มพิกัด' : `แก้พิกัด ${f.hs_code}`} sub="อัตราอากร/มาตรการต่อ HS 11 หลัก" onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="frow">
          <Fld label="HS 11 หลัก"><input value={f.hs_code} onChange={set('hs_code')} disabled={!isNew} required/></Fld>
          <Fld label="อากร %"><input type="number" step="0.001" value={f.duty_rate ?? ''} onChange={set('duty_rate')} placeholder="เว้น = ยังไม่ตรวจ"/></Fld>
          <Fld label="VAT %"><input type="number" step="0.1" value={f.vat_rate ?? 7} onChange={set('vat_rate')}/></Fld>
          <Fld label="ตรวจล่าสุด"><input type="date" value={f.last_verified ?? ''} onChange={set('last_verified')}/></Fld>
        </div>
        <Fld label="คำอธิบายไทย"><input value={f.description_th ?? ''} onChange={set('description_th')}/></Fld>
        <Fld label="measures (JSON) — permit ต่อหน่วยงาน" hint='[{"req_type":"permit_tisi","side":"import","needed_by":"before_clear","note":"…"}]'>
          <textarea value={measuresText} onChange={set('measures')} rows={4} style={{ fontFamily: 'var(--mono)', fontSize: 12 }}/>
        </Fld>
        <div className="frow">
          <Fld label="FTA notes"><input value={f.fta_notes ?? ''} onChange={set('fta_notes')}/></Fld>
          <Fld label="ที่มา (source_url)"><input value={f.source_url ?? ''} onChange={set('source_url')}/></Fld>
        </div>
        <Fld label="หมายเหตุ/caveat"><input value={f.notes ?? ''} onChange={set('notes')}/></Fld>
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot">
          <button type="button" className="btn2" onClick={onClose}>ยกเลิก</button>
          <button className="btn1" disabled={busy}>{busy ? 'บันทึก…' : 'บันทึก'}</button>
        </div>
      </form>
    </Modal>
  );
}

// หน้าย่อยของ "ตั้งค่า" — อยู่ใน URL /settings/<zone> (รีเฟรชแล้วอยู่หน้าย่อยเดิม · lib/route.js)
const ZONES = ['people', 'trash', 'rules', 'fta', 'audit'];

function Settings({ initZone, onZone }) {
  const [zone, setZoneRaw] = useState(ZONES.includes(initZone) ? initZone : 'people');
  const setZone = (z) => { setZoneRaw(z); onZone?.(z === 'people' ? undefined : z); };
  // เข้ามาด้วยหน้าย่อยที่ไม่มีจริง (/settings/xyz) → URL กลับเป็นหน้าย่อยที่โชว์อยู่
  useEffect(() => { if (initZone && !ZONES.includes(initZone)) onZone?.(undefined); }, []);
  const [rules, setRules] = useState([]);
  const [fta, setFta] = useState([]);
  const [audit, setAudit] = useState([]);
  const [editing, setEditing] = useState(null);   // rule | 'new'
  const [error, setError] = useState(null);
  const [importMsg, setImportMsg] = useState(null);
  const fileRef = useRef(null);

  const [pending, setPending] = useState(false);
  const loadRules = async () => { try { setRules(await db.searchHsRules('')); } catch (e) { if (isSchemaPending(e)) setPending(true); else setError(e.message ?? String(e)); } };
  useEffect(() => { loadRules(); db.listFtaForms().then(setFta).catch(() => {}); db.auditLog(80).then(setAudit).catch(() => {}); }, []);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setImportMsg('กำลังอ่านไฟล์…'); setError(null);
    try {
      const recs = fileToHsRecords(await file.text());
      if (!recs.length) throw new Error('ไม่พบข้อมูล — หัวคอลัมน์ต้องมี hs_code / พิกัด (ดาวน์โหลดเทมเพลตดูตัวอย่าง)');
      const { added, skipped } = await db.bulkHsRules(recs);
      setImportMsg(`เพิ่มพิกัดใหม่ ${added} รายการ${skipped ? ` · ข้าม ${skipped} ที่มีอยู่แล้ว (แก้ทีละพิกัดได้)` : ''}`);
      await loadRules();
      setTimeout(() => setImportMsg(null), 5000);
    } catch (er) { setImportMsg(null); setError(er.message ?? String(er)); }
  };
  const downloadHsTemplate = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + HS_TEMPLATE], { type: 'text/csv' }));
    a.download = 'template_พิกัด.csv'; a.click(); URL.revokeObjectURL(a.href);
  };

  if (pending) return <DbPending onRetry={loadRules}/>;

  const staleCount = rules.filter(r => staleRule(r).stale).length;

  return (
    <div className="settings">
      <div className="setzones">
        <button className={`setzone ${zone === 'people' ? 'on' : ''}`} onClick={() => setZone('people')}>ผู้ใช้ & สิทธิ์</button>
        <button className={`setzone ${zone === 'trash' ? 'on' : ''}`} onClick={() => setZone('trash')}>ถังขยะ</button>
        <button className={`setzone ${zone === 'rules' ? 'on' : ''}`} onClick={() => setZone('rules')}>กฎพิกัด & ข้อกำหนด</button>
        <button className={`setzone ${zone === 'fta' ? 'on' : ''}`} onClick={() => setZone('fta')}>FTA form ต่อประเทศ</button>
        <button className={`setzone ${zone === 'audit' ? 'on' : ''}`} onClick={() => setZone('audit')}>Audit log</button>
      </div>
      {error && <div className="ferr">{error}</div>}

      {zone === 'people' && <People/>}
      {zone === 'trash' && <Trash/>}

      {zone === 'rules' && (
        <>
          <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={onImport}/>
          <div className="page-h">
            <span className="meta">{rules.length} พิกัด{staleCount ? ` · ${staleCount} ควร re-verify` : ''}</span>
            <button className="btn2" style={{ marginLeft: 'auto' }} onClick={downloadHsTemplate}>เทมเพลต</button>
            <button className="btn2" onClick={() => fileRef.current?.click()}>นำเข้าไฟล์</button>
            <button className="btn1" onClick={() => setEditing('new')}>＋ เพิ่มพิกัด</button>
          </div>
          {importMsg && <div className="importmsg">{importMsg}</div>}
          <div className="rule-table">
            {rules.map(r => {
              const st = staleRule(r);
              return (
                <button className="rule-row" key={r.hs_code} onClick={() => setEditing(r)}>
                  <span className="rr-code">{r.hs_code}</span>
                  <span className="rr-desc">{r.description_th}</span>
                  <span className="rr-duty">{r.duty_rate == null ? '—' : `${r.duty_rate}%`}</span>
                  <span className="rr-m">{(r.measures ?? []).length ? `${r.measures.length} มาตรการ` : ''}</span>
                  {st.stale && <span className="rr-stale">● re-verify</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {zone === 'fta' && (
        <div className="fta-table">
          {fta.map(f => (
            <div className="fta-row" key={f.origin_country}>
              <span className="fr-cc">{f.origin_country}</span>
              <span className="fr-form">Form {f.form_type}</span>
              <span className="fr-name">{f.fta_name}</span>
              {!f.active && <span className="fr-off">ปิด</span>}
            </div>
          ))}
          {!fta.length && <div className="jr-empty">—</div>}
        </div>
      )}

      {zone === 'audit' && (
        <div className="audit">
          {audit.map(e => (
            <div className="audit-row" key={e.id}>
              <span className="au-code">{e.shipment?.code ?? '—'}</span>
              <span className="au-type">{e.event_type === 'note' ? `บันทึก: ${e.payload?.text ?? ''}` : e.event_type}</span>
              <span className="au-role">{e.actor_role ?? ''}</span>
              <span className="au-time">{new Date(e.created_at).toLocaleString('th-TH')}</span>
            </div>
          ))}
          {!audit.length && <div className="jr-empty">—</div>}
        </div>
      )}

      {editing && (
        <HsRuleEditor rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={loadRules}/>
      )}
    </div>
  );
}

export default function Admin({ view, zone, onZone }) {
  if (MOCK) return (
    <div className="placeholder">
      <h2>{view === 'settings' ? 'Settings' : 'Analytics'}</h2>
      <p>โหมด MOCK — ต่อ Supabase (บัญชี admin) เพื่อดูสถิติจริง / จัดการกฎพิกัด</p>
    </div>
  );
  return (
    <div className="page">
      <div className="page-h"><h2>{view === 'settings' ? 'ตั้งค่า' : 'สถิติการนำเข้า'}</h2></div>
      {view === 'settings' ? <Settings initZone={zone} onZone={onZone}/> : <Analytics/>}
    </div>
  );
}
